import { DeliveryZone } from '@@types'
import { useCallback } from 'react'

import { type Brand, useAddressStore } from '@entities/Address'
import type { AvailableBrandInfo } from '@entities/DeliveryZone'
import { useDeliveryStore } from '@entities/DeliveryZone/model/useDeliveryStore'
import type { Slot } from '@entities/Slots'

import { useSlotStore } from '@widgets/slots'

/**
 * Хук для получения данных о доставке.
 * Просто читает из Zustand store — не делает никаких запросов.
 * Все запросы делаются в DeliveryManager.
 */
export function useDelivery () {
  // Данные из address store
  const selectedAddress = useAddressStore((state) => state.selectedAddress)
  const shopId = useAddressStore((state) => state.shopId)
  const zoneId = useAddressStore((state) => state.zoneId)
  const brand = useAddressStore((state) => state.brand)
  const geoPoint = useAddressStore((state) => state.geoPoint)
  const deliveryType = useAddressStore((state) => state.deliveryType)
  const isPickup = useAddressStore((state) => state.isPickup)
  const setZoneId = useAddressStore((state) => state.setZoneId)
  const setShopId = useAddressStore((state) => state.setShopId)
  const setBrand = useAddressStore((state) => state.setBrand)

  // Данные из slot store
  const selectedSlot = useSlotStore((state) => state.selectedSlot)
  const setSelectedSlot = useSlotStore((state) => state.setSelectedSlot)
  const clearSelectedSlot = useSlotStore((state) => state.clearSelectedSlot)

  // Данные из delivery store (подписка на конкретные поля)
  const loading = useDeliveryStore((state) => state.loading)
  const loadingZones = useDeliveryStore((state) => state.loadingZones)
  const loadingSlots = useDeliveryStore((state) => state.loadingSlots)
  const deliveryZones = useDeliveryStore((state) => state.deliveryZones)
  const currentZone = useDeliveryStore((state) => state.currentZone)
  const availableBrands = useDeliveryStore((state) => state.availableBrands)
  const slots = useDeliveryStore((state) => state.slots)
  const hasDelivery = useDeliveryStore((state) => state.hasDelivery)
  const isSlotDeliveryAvailable = useDeliveryStore((state) => state.isSlotDeliveryAvailable)
  const isExpressDeliveryAvailable = useDeliveryStore((state) => state.isExpressDeliveryAvailable)
  const deliveryCost = useDeliveryStore((state) => state.deliveryCost)
  const minOrderCost = useDeliveryStore((state) => state.minOrderCost)
  const freeSlotDeliveryCostFrom = useDeliveryStore((state) => state.freeSlotDeliveryCostFrom)
  const maxSlotWeight = useDeliveryStore((state) => state.maxSlotWeight)
  const maxExpressWeight = useDeliveryStore((state) => state.maxExpressWeight)

  // Выбор бренда
  const selectBrand = useCallback((newBrand: Brand) => {
    setBrand(newBrand)
    
    const zoneForBrand = deliveryZones.find(
      (z) => z.shop?.brand?.id === newBrand.id,
    )
    
    if (zoneForBrand) {
      setZoneId(zoneForBrand.id)
      if (zoneForBrand.shop_id) {
        setShopId(zoneForBrand.shop_id)
      }
      clearSelectedSlot()
    }
  }, [ deliveryZones, setZoneId, setShopId, setBrand, clearSelectedSlot ])

  // Ближайший слот
  const nearestSlot = slots.length > 0 ? slots[0] : null

  return {
    // Состояние загрузки
    loading,
    loadingZones,
    loadingSlots,
    zonesError: null, // TODO: добавить в store если нужно
    slotsError: null,

    // Зоны и бренды
    deliveryZones,
    availableBrands,
    currentZone,
    hasDelivery,
    hasBrandSelection: availableBrands.length > 1,

    // Слоты
    slots,
    nearestSlot,
    selectedSlot,
    setSelectedSlot,
    isSlotDeliveryAvailable,

    // Текущие значения
    shopId,
    zoneId,
    brand,
    selectedAddress,
    deliveryType,
    geoPoint,

    // Параметры доставки
    deliveryCost,
    minOrderCost,
    freeSlotDeliveryCostFrom,
    isExpressDeliveryAvailable,

    maxSlotWeight,
    maxExpressWeight,

    // Утилиты
    isPickup,

    // Действия
    selectBrand,
    reloadDeliveryInfo: () => {}, // TODO: добавить refetch
    refetchZones: () => {},
    refetchSlots: () => {},
  }
}

export default useDelivery

export type { DeliveryZone, AvailableBrandInfo, Slot }
